import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiCheck, FiExternalLink } from 'react-icons/fi';
import { Button } from '@/components/Button';
import { Footer, SEO } from '@/components';
import { EmailGateModal, PlanPreview, WeekGrid, WizardShell } from '@/components/parenting-plan';
import { Input } from '@/components/Input';
import { API_BASE_URL, apiJson, extractApiErrorMessage } from '@/lib/api';
import { track } from '@/lib/analytics';
import type { ParentingPlanAnswers } from '@/lib/parenting-plan-schemas';
import { SCHEDULE_PAGE_META, isScheduleSlug } from '@/lib/parenting-plan-schemas';

// Value-first ordering: pick the schedule (the aha) before anything else;
// children are optional and later; download email is captured at the end.
const STEPS = [
  { id: 'custody', label: 'Custody' },
  { id: 'holidays', label: 'Holidays' },
  { id: 'communication', label: 'Communication' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'children', label: 'Children' },
  { id: 'review', label: 'Review' },
] as const;

type Option = { value: string; label: string };

function ChoiceChips({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Option[];
  value?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <p className='mb-2 text-sm font-medium text-gray-700'>{label}</p>
      <div className='flex flex-wrap gap-2'>
        {options.map((o) => (
          <button
            key={o.value}
            type='button'
            onClick={() => onChange(o.value)}
            className={clsx(
              'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
              value === o.value
                ? 'border-primary bg-primary text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:border-primary',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/parenting-plan/builder')({
  validateSearch: (search: Record<string, unknown>) => ({
    claimToken: typeof search.claimToken === 'string' ? search.claimToken : undefined,
    schedule: typeof search.schedule === 'string' ? search.schedule : undefined,
  }),
  component: ParentingPlanBuilderPage,
});

function ParentingPlanBuilderPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: Route.fullPath });
  const { isLoaded: authLoaded, isSignedIn, getToken } = useAuth();

  const [step, setStep] = useState(0);
  const [claimToken, setClaimToken] = useState<string | null>(search.claimToken ?? null);
  const [answers, setAnswers] = useState<ParentingPlanAnswers>({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [downloaded, setDownloaded] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadGeneration = useRef(0);
  const createdTracked = useRef(false);

  const persistSearch = useCallback(
    (token: string) => {
      void navigate({
        to: '/parenting-plan/builder',
        search: { claimToken: token, schedule: undefined },
        replace: true,
      });
    },
    [navigate],
  );

  // Initial load: resume draft or create new
  useEffect(() => {
    const gen = ++loadGeneration.current;
    let cancelled = false;
    (async () => {
      try {
        if (search.claimToken) {
          const res = await fetch(`${API_BASE_URL}/v1/parenting-plan/draft/${search.claimToken}`);
          if (!res.ok) throw new Error('Draft not found');
          const data = (await res.json()) as {
            claim_token: string;
            schedule_type: string | null;
            answers: ParentingPlanAnswers;
          };
          if (cancelled || gen !== loadGeneration.current) return;
          setClaimToken(data.claim_token);
          setAnswers(data.answers || {});
          track('Parenting Plan Draft Resumed');
          return;
        }

        const initialSchedule =
          search.schedule && isScheduleSlug(search.schedule) ? search.schedule : undefined;
        const res = await fetch(`${API_BASE_URL}/v1/parenting-plan/draft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            schedule_type: initialSchedule,
            answers: initialSchedule
              ? { custody: { scheduleSlug: initialSchedule } }
              : {},
          }),
        });
        if (!res.ok) throw new Error('Could not start draft');
        const data = (await res.json()) as { claim_token: string; answers: ParentingPlanAnswers };
        if (cancelled || gen !== loadGeneration.current) return;
        setClaimToken(data.claim_token);
        setAnswers(data.answers || {});
        persistSearch(data.claim_token);
      } catch {
        if (!cancelled && gen === loadGeneration.current) {
          setLoadError('We could not load your draft. Please refresh or start from the hub.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search.claimToken, search.schedule, persistSearch]);

  useEffect(() => {
    track('Parenting Plan Builder View');
  }, []);

  // Fire "Draft Created" only once the visitor actually answers something —
  // the draft row is auto-created on open, so open-count is not a completion.
  useEffect(() => {
    if (createdTracked.current) return;
    const substantive = Boolean(
      answers.custody?.scheduleSlug ||
        answers.holidays?.notes ||
        answers.communication?.channel ||
        answers.communication?.responseTime ||
        answers.expenses?.split ||
        answers.decisions?.notes ||
        answers.children?.items?.some((c) => c.name?.trim()),
    );
    if (substantive) {
      createdTracked.current = true;
      track('Parenting Plan Draft Created');
    }
  }, [answers]);

  const flushSave = useCallback(async () => {
    if (!claimToken) return;
    setSaving(true);
    try {
      await fetch(`${API_BASE_URL}/v1/parenting-plan/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_token: claimToken,
          schedule_type: answers.custody?.scheduleSlug ?? undefined,
          answers,
        }),
      });
    } finally {
      setSaving(false);
    }
  }, [claimToken, answers]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void flushSave();
    }, 600);
  }, [flushSave]);

  useEffect(() => {
    if (!claimToken) return;
    scheduleSave();
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [answers, claimToken, scheduleSave]);

  const handleDownload = async (email: string) => {
    if (!claimToken) return;
    setGenerateLoading(true);
    try {
      track('Parenting Plan Email Submitted');
      const w = window as Window & {
        gtagSendEvent?: (eventName: string) => void;
        fbq?: (...args: unknown[]) => void;
      };
      if (typeof w.gtagSendEvent === 'function') {
        w.gtagSendEvent('generate_lead');
      }
      if (typeof w.fbq === 'function') {
        w.fbq('track', 'Lead');
      }

      await flushSave();

      const res = await fetch(`${API_BASE_URL}/v1/parenting-plan/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claim_token: claimToken, email }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(t || 'Generate failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'parenting-plan.pdf';
      a.click();
      URL.revokeObjectURL(url);
      track('Parenting Plan PDF Downloaded');
      setSubmittedEmail(email);
      setDownloaded(true);
      setEmailOpen(false);
    } finally {
      setGenerateLoading(false);
    }
  };

  const canNext = (() => {
    if (STEPS[step].id === 'custody') {
      return Boolean(answers.custody?.scheduleSlug && answers.custody?.startDate);
    }
    return true;
  })();

  const isLastStep = step === STEPS.length - 1;
  const nextLabel = isLastStep ? 'Download PDF' : 'Continue';

  const goNext = async () => {
    if (isLastStep) {
      await flushSave();
      track('Parenting Plan Step Completed', { step: STEPS[step]?.id });
      setEmailOpen(true);
      return;
    }
    track('Parenting Plan Step Completed', { step: STEPS[step]?.id });
    setStep((s) => s + 1);
  };

  const goBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  const nunito = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  };

  const seo = (
    <SEO
      title='Parenting plan builder | Harbor'
      description='Draft your parenting plan in Harbor. Walk through custody time, holidays, communication, and shared expenses, then download a printable PDF.'
      path='/parenting-plan/builder'
      noindex
    />
  );

  if (loadError) {
    return (
      <div className='min-h-screen bg-white px-6 py-24 text-center'>
        {seo}
        <p className='text-gray-700'>{loadError}</p>
        <Link to='/parenting-plan' className='mt-6 inline-block text-primary font-semibold'>
          Back to hub
        </Link>
      </div>
    );
  }

  if (!claimToken) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white'>
        {seo}
        <p className='text-gray-600'>Starting your plan…</p>
      </div>
    );
  }

  if (downloaded && claimToken) {
    return (
      <SuccessScreen
        seo={seo}
        nunito={nunito}
        claimToken={claimToken}
        submittedEmail={submittedEmail}
        canClaim={Boolean(authLoaded && isSignedIn)}
        getToken={getToken}
      />
    );
  }

  const slug = answers.custody?.scheduleSlug;
  const meta = slug && isScheduleSlug(slug) ? SCHEDULE_PAGE_META[slug] : null;

  return (
    <div className='min-h-screen bg-gray-50'>
      {seo}
      <header className='border-b border-gray-200 bg-white'>
        <div className='mx-auto flex max-w-6xl items-center justify-between px-4 py-4'>
          <Link
            to='/parenting-plan'
            className='inline-flex items-center gap-2 text-sm font-medium text-primary'
            style={nunito}
          >
            <FiArrowLeft /> Parenting plan hub
          </Link>
          <span className='text-xs text-gray-500'>{saving ? 'Saving…' : 'Saved'}</span>
        </div>
      </header>

      <WizardShell
        steps={[...STEPS]}
        activeIndex={step}
        onBack={goBack}
        onNext={goNext}
        canGoBack={step > 0}
        canGoNext={canNext}
        nextLabel={nextLabel}
        sidePanel={
          <div className='rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:sticky lg:top-24'>
            <PlanPreview answers={answers} />
          </div>
        }
      >
        {STEPS[step].id === 'custody' && (
          <CustodyStep answers={answers} setAnswers={setAnswers} meta={meta} />
        )}

        {STEPS[step].id === 'holidays' && (
          <SectionShell title='Holidays & school breaks' subtitle='Pick the rule that matches what you want to propose.'>
            <ChoiceChips
              label='How should holidays and breaks work?'
              value={answers.holidays?.notes}
              onChange={(v) => setAnswers((a) => ({ ...a, holidays: { notes: v } }))}
              options={[
                { value: 'Major holidays and school breaks alternate each year.', label: 'Alternate each year' },
                { value: 'Each holiday and break is assigned to one designated parent.', label: 'Assign to one parent' },
                { value: 'Major holidays and school breaks are split between the parents.', label: 'Split between parents' },
              ]}
            />
          </SectionShell>
        )}

        {STEPS[step].id === 'communication' && (
          <SectionShell title='Communication' subtitle='Keep child-related coordination in one written channel.'>
            <ChoiceChips
              label='Preferred written channel'
              value={answers.communication?.channel}
              onChange={(v) =>
                setAnswers((a) => ({ ...a, communication: { ...a.communication, channel: v } }))
              }
              options={[
                { value: 'Harbor chat', label: 'Harbor chat' },
                { value: 'Email', label: 'Email' },
                { value: 'Text message', label: 'Text message' },
              ]}
            />
            <ChoiceChips
              label='Expected response time (non-emergency)'
              value={answers.communication?.responseTime}
              onChange={(v) =>
                setAnswers((a) => ({ ...a, communication: { ...a.communication, responseTime: v } }))
              }
              options={[
                { value: '24 hours', label: 'Within 24 hours' },
                { value: '48 hours', label: 'Within 48 hours' },
                { value: 'Emergencies only', label: 'Emergencies only' },
              ]}
            />
          </SectionShell>
        )}

        {STEPS[step].id === 'expenses' && (
          <SectionShell title='Shared expenses' subtitle='Set how shared child-related costs are split.'>
            <ChoiceChips
              label='Default split'
              value={answers.expenses?.split}
              onChange={(v) => setAnswers((a) => ({ ...a, expenses: { ...a.expenses, split: v } }))}
              options={[
                { value: '50/50', label: '50/50' },
                { value: 'Proportional to income', label: 'Proportional to income' },
                { value: 'Each parent pays their own', label: 'Each pays their own' },
              ]}
            />
          </SectionShell>
        )}

        {STEPS[step].id === 'decisions' && (
          <SectionShell title='Decisions & disputes' subtitle='How major decisions and disagreements get resolved.'>
            <ChoiceChips
              label='Decision-making approach'
              value={answers.decisions?.notes}
              onChange={(v) => setAnswers((a) => ({ ...a, decisions: { notes: v } }))}
              options={[
                { value: 'Major decisions require mutual agreement; disputes go to mediation before court.', label: 'Mutual agreement, mediation first' },
                { value: 'Each parent makes day-to-day decisions during their own parenting time.', label: 'Day-to-day during own time' },
                { value: 'Disputes are resolved through a parenting coordinator or mediator.', label: 'Use a coordinator/mediator' },
              ]}
            />
          </SectionShell>
        )}

        {STEPS[step].id === 'children' && (
          <ChildrenStep answers={answers} setAnswers={setAnswers} />
        )}

        {STEPS[step].id === 'review' && (
          <div className='space-y-6'>
            <div>
              <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
                Review & download
              </h2>
              <p className='mt-2 text-gray-600' style={nunito}>
                Preview your plan on the right, then download a printable PDF. We&apos;ll email you a
                copy with a link to open Harbor and import your custody pattern.
              </p>
            </div>
            <div className='lg:hidden'>
              <PlanPreview answers={answers} />
            </div>
            <a
              href='https://apps.apple.com/app/harbor-co-parenting/id6741088558'
              target='_blank'
              rel='noreferrer'
              className='inline-flex items-center rounded-2xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-800'
            >
              Get Harbor app
            </a>
          </div>
        )}
      </WizardShell>

      <EmailGateModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        onSubmit={handleDownload}
        loading={generateLoading}
      />

      <Footer />
    </div>
  );
}

function SectionShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  const nunito = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  };
  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
          {title}
        </h2>
        {subtitle ? (
          <p className='mt-2 text-gray-600' style={nunito}>
            {subtitle}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function SuccessScreen({
  seo,
  nunito,
  claimToken,
  submittedEmail,
  canClaim,
  getToken,
}: {
  seo: React.ReactNode;
  nunito: React.CSSProperties;
  claimToken: string;
  submittedEmail: string;
  canClaim: boolean;
  getToken: () => Promise<string | null>;
}) {
  const [coParentEmail, setCoParentEmail] = useState('');
  const [inviteState, setInviteState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [claimState, setClaimState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [claimMessage, setClaimMessage] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);

  const sendInvite = async () => {
    const email = coParentEmail.trim();
    if (!email.includes('@')) {
      setInviteError('Please enter a valid email.');
      return;
    }
    setInviteError(null);
    setInviteState('sending');
    try {
      const res = await fetch(`${API_BASE_URL}/v1/parenting-plan/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_token: claimToken,
          email: submittedEmail,
          co_parent_email: email,
        }),
      });
      if (!res.ok) throw new Error('send failed');
      track('Parenting Plan Co-parent Invited');
      setInviteState('sent');
    } catch {
      setInviteState('idle');
      setInviteError('Could not send. Please try again.');
    }
  };

  const claim = async () => {
    setClaimError(null);
    setClaimState('saving');
    try {
      const res = await apiJson<{
        success: boolean;
        custody_schedule_created: boolean;
        message?: string | null;
      }>(getToken, '/v1/parenting-plan/claim', {
        method: 'POST',
        body: JSON.stringify({ claim_token: claimToken }),
      });
      track('Parenting Plan Claimed');
      if (res.custody_schedule_created) {
        track('Parenting Plan Schedule Imported');
      }
      setClaimMessage(res.message ?? 'Saved to your Harbor account.');
      setClaimState('saved');
    } catch (err) {
      setClaimState('idle');
      setClaimError(extractApiErrorMessage(err, 'Could not save your plan. Please try again.'));
    }
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      {seo}
      <div className='mx-auto max-w-2xl px-6 py-16'>
        <div className='rounded-2xl border border-gray-200 bg-white p-8 shadow-sm'>
          <div className='mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary'>
            <FiCheck className='h-6 w-6' />
          </div>
          <h1 className='text-2xl font-bold text-gray-900' style={nunito}>
            Your plan is ready
          </h1>
          <p className='mt-2 text-gray-600' style={nunito}>
            We&apos;ve emailed a copy of your parenting plan PDF. You can keep editing this draft
            anytime with your link.
          </p>

          <div className='mt-8 rounded-xl border border-gray-200 p-5'>
            <h2 className='text-lg font-semibold text-gray-900' style={nunito}>
              Propose it to your co-parent
            </h2>
            <p className='mt-1 text-sm text-gray-600' style={nunito}>
              Send the draft to the other parent to review. They&apos;ll get the PDF and a link back
              to this plan.
            </p>
            {inviteState === 'sent' ? (
              <p className='mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary'>
                <FiCheck /> Sent to {coParentEmail.trim()}
              </p>
            ) : (
              <div className='mt-4 space-y-3'>
                <Input
                  type='email'
                  placeholder="co-parent@example.com"
                  value={coParentEmail}
                  onChange={(e) => setCoParentEmail(e.target.value)}
                />
                {inviteError ? <p className='text-sm text-red-600'>{inviteError}</p> : null}
                <Button
                  type='button'
                  onClick={() => void sendInvite()}
                  loading={inviteState === 'sending'}
                  disabled={inviteState === 'sending'}
                >
                  Email the draft
                </Button>
              </div>
            )}
          </div>

          <div className='mt-6 rounded-xl border border-gray-200 p-5'>
            <h2 className='text-lg font-semibold text-gray-900' style={nunito}>
              Save to your Harbor account
            </h2>
            <p className='mt-1 text-sm text-gray-600' style={nunito}>
              Import this custody pattern into your Harbor calendar.
            </p>
            {claimMessage ? (
              <p className='mt-4 text-sm font-medium text-primary'>{claimMessage}</p>
            ) : canClaim ? (
              <div className='mt-4 space-y-3'>
                {claimError ? <p className='text-sm text-red-600'>{claimError}</p> : null}
                <Button
                  type='button'
                  onClick={() => void claim()}
                  loading={claimState === 'saving'}
                  disabled={claimState === 'saving'}
                >
                  Save to my account
                </Button>
              </div>
            ) : (
              <Link
                to='/auth/sign-in'
                className='mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary'
                style={nunito}
              >
                Sign in to save <FiExternalLink />
              </Link>
            )}
          </div>

          <p className='mt-8 text-sm text-gray-500' style={nunito}>
            <Link to='/parenting-plan' className='text-primary font-semibold'>
              Back to the parenting plan hub
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function ChildrenStep({
  answers,
  setAnswers,
}: {
  answers: ParentingPlanAnswers;
  setAnswers: React.Dispatch<React.SetStateAction<ParentingPlanAnswers>>;
}) {
  const items = answers.children?.items?.length ? answers.children.items : [{ name: '', birthYear: '' }];

  const update = (idx: number, field: 'name' | 'birthYear', value: string) => {
    setAnswers((a) => {
      const next = [...(a.children?.items ?? [{ name: '', birthYear: '' }])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...a, children: { items: next } };
    });
  };

  const add = () => {
    setAnswers((a) => ({
      ...a,
      children: { items: [...(a.children?.items ?? []), { name: '', birthYear: '' }] },
    }));
  };

  return (
    <div>
      <h2 className='mb-2 text-2xl font-bold text-gray-900'>Children</h2>
      <p className='mb-6 text-gray-600'>
        Optional — add your kids&apos; first names (no dates needed) if you want them in the plan.
      </p>
      <div className='space-y-4'>
        {items.map((row, idx) => (
          <div key={idx} className='flex flex-wrap gap-3'>
            <div className='min-w-[200px] flex-1'>
              <label className='mb-1 block text-sm text-gray-600'>First name</label>
              <Input value={row.name ?? ''} onChange={(e) => update(idx, 'name', e.target.value)} />
            </div>
            <div className='w-32'>
              <label className='mb-1 block text-sm text-gray-600'>Birth year</label>
              <Input value={row.birthYear ?? ''} onChange={(e) => update(idx, 'birthYear', e.target.value)} />
            </div>
          </div>
        ))}
      </div>
      <button type='button' className='mt-4 text-sm font-semibold text-primary' onClick={add}>
        + Add another child
      </button>
    </div>
  );
}

function CustodyStep({
  answers,
  setAnswers,
  meta,
}: {
  answers: ParentingPlanAnswers;
  setAnswers: React.Dispatch<React.SetStateAction<ParentingPlanAnswers>>;
  meta: (typeof SCHEDULE_PAGE_META)[keyof typeof SCHEDULE_PAGE_META] | null;
}) {
  const slug = answers.custody?.scheduleSlug ?? '';
  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-2xl font-bold text-gray-900'>Custody schedule</h2>
        <p className='mt-2 text-gray-600'>Pick the pattern that best matches what you want to propose.</p>
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Schedule type</label>
        <select
          className='w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-3'
          value={slug}
          onChange={(e) =>
            setAnswers((a) => ({
              ...a,
              custody: { ...a.custody, scheduleSlug: e.target.value },
            }))
          }
        >
          <option value=''>Select…</option>
          <option value='2-2-3'>2-2-3</option>
          <option value='2-2-5-5'>2-2-5-5</option>
          <option value='week-on-week-off'>Week on / week off</option>
          <option value='3-4-4-3'>3-4-4-3</option>
          <option value='50-50'>50/50 (week-based)</option>
          <option value='60-40'>60/40</option>
          <option value='long-distance'>Long distance</option>
          <option value='newborn'>Newborn-focused</option>
          <option value='first-time'>First parenting plan</option>
          <option value='holidays-only'>Holidays & breaks focus</option>
          <option value='summer'>Summer / extended time</option>
          <option value='every-extended-weekend'>Extended weekends</option>
        </select>
      </div>
      {meta ? (
        <div className='max-w-md'>
          <WeekGrid pattern={meta.pattern14} parentALabel='Parent A' parentBLabel='Parent B' />
        </div>
      ) : null}
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Pattern start date</label>
        <Input
          type='date'
          value={answers.custody?.startDate ?? ''}
          onChange={(e) =>
            setAnswers((a) => ({
              ...a,
              custody: { ...a.custody, startDate: e.target.value },
            }))
          }
        />
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Pick-up / drop-off notes (optional)</label>
        <textarea
          className='w-full min-h-[100px] rounded-xl border border-gray-200 p-4'
          value={answers.custody?.notes ?? ''}
          onChange={(e) =>
            setAnswers((a) => ({
              ...a,
              custody: { ...a.custody, notes: e.target.value },
            }))
          }
        />
      </div>
    </div>
  );
}
